import { localFetch } from '../../../../lib/local_fetch'
import { shopifyGql, ShopifyGqlUserErrors } from '../shopify_client/graphql'
import {
  IBuildCheckoutAttributes,
  ICart,
  ICartDeliveryAddress,
  ICountryCode,
  IShippingAddress
} from '../shopify_client/types'
import { checkAddressSufficient } from '../shopify_client/address_utils'
import { cartFragment } from './fragments'
import countries from 'i18n-iso-countries'
import countriesEnglish from 'i18n-iso-countries/langs/en.json'

countries.registerLocale(countriesEnglish)

export const shippingAdddressToCartDeliveryAddressInput = (shippingAddress: IShippingAddress): ICartDeliveryAddress => {
  const {
    address1,
    address2,
    city,
    company,
    firstName,
    lastName,
    phone,
    country,
    zip
  } = shippingAddress

  const countryCode = countries.getAlpha2Code(country, "en") as ICountryCode

  return {
    address1,
    address2,
    city,
    company,
    countryCode,
    firstName,
    lastName,
    phone,
    provinceCode: shippingAddress.province,
    zip
  }
}

async function logCartLocally(
  { buildCart, cart }: { buildCart: IBuildCheckoutAttributes, cart: ICart },
): Promise<void> {
  const payload = new FormData()

  payload.append('cart[account_id]', buildCart.accountID.toString())
  if (buildCart.userID) {
    payload.append('cart[user_id]', buildCart.userID.toString())
  }
  payload.append('cart[encoded_shopify_checkout_id]', cart.id)

  const resp = await localFetch(
    buildCart.checkoutLogRoute, {
    method: 'POST',
    body: JSON.stringify({
      cart: {
        account_id: buildCart.accountID,
        user_id: buildCart.userID,
        shopify_cart_id: cart.id,
      },
    }),
  },
  )

  if (resp.status >= 300) {
    throw new Error(`Unexpected response status: ${resp.status}`)
  }
}

export async function createCart(
  buildCart: IBuildCheckoutAttributes
): Promise<Partial<ICart>> {

  const cartInput = {
    ...(buildCart.email && {
      buyerIdentity: { email: buildCart.email }
    }),
    ...(checkAddressSufficient(buildCart.shippingAddress) && {
      delivery: {
        addresses: [{
          address: {
            deliveryAddress: shippingAdddressToCartDeliveryAddressInput(buildCart.shippingAddress)
          },
          selected: true
        }]
      }
    })
  }

  const { data, errors } = await shopifyGql<ICreateCartData>(
    createCartMutation,
    { cartInput }
  )

  if (errors.length > 0) { throw new AggregateError(errors, 'createCart.cartCreate') }

  const cart: ICart = data.cartCreate.cart

  await logCartLocally({ buildCart, cart })
  return cart
}

export interface ICreateCartData {
  cartCreate: {
    cart: {
      __typename: 'Cart',
    } & ICart
  } & ShopifyGqlUserErrors
}

const createCartMutation = `
mutation cartCreate($cartInput: CartInput!) {
  cartCreate(input: $cartInput) {
    cart {
      ...CartFragment
    }
    userErrors {
      field
      message
    }
  }
}
${cartFragment}
`
